const EARLIEST_BIRTH_YEAR = 1900;

const toNumber = (value) => (/^\d+$/.test(String(value ?? "").trim()) ? Number(value) : NaN);

/**
 * Whether day, month and year name a real date, no earlier than 1900 and not in the future.
 * Built from Date.UTC and read back, so 31 April or 29 February 2023 fail rather than roll
 * over into the next month.
 */
export const isValidDateOfBirth = ({ day, month, year, today = new Date() }) => {
  const dayNumber = toNumber(day);
  const monthNumber = toNumber(month);
  const yearNumber = toNumber(year);

  if ([dayNumber, monthNumber, yearNumber].some(Number.isNaN)) return false;

  if (String(year).trim().length !== 4 || yearNumber < EARLIEST_BIRTH_YEAR) return false;

  const date = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber));
  const isRealDate =
    date.getUTCFullYear() === yearNumber &&
    date.getUTCMonth() === monthNumber - 1 &&
    date.getUTCDate() === dayNumber;

  if (!isRealDate) return false;

  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

  return date.getTime() <= todayUtc;
};

/**
 * The date as the backend reads it, `yyyy-MM-dd` with no time or timezone. Formatted by hand:
 * `toISOString()` converts to UTC first, which moves an Irish summer-time midnight back a day
 * and the patient's date of birth with it.
 */
export const formatDateOfBirth = ({ day, month, year }) =>
  [
    String(Number(year)).padStart(4, "0"),
    String(Number(month)).padStart(2, "0"),
    String(Number(day)).padStart(2, "0"),
  ].join("-");
